import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import useTranslate from 'hooks/useTranslate';
import type { AssessmentBillingRule } from 'types';
import { currencyOptions } from 'utils/currencyOptions';
import { assessmentBillingValidationSchema } from 'utils/validations/serviceProviderBilling';

interface AssessmentBillingDialogProps {
    rule: AssessmentBillingRule | null;
    loading: boolean;
    onClose: () => void;
    onSubmit: (rule: AssessmentBillingRule) => void;
}

const AssessmentBillingDialog = ({
    rule,
    loading,
    onClose,
    onSubmit,
}: AssessmentBillingDialogProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const validationSchema = useMemo(
        () => assessmentBillingValidationSchema(translate),
        [translate]
    );

    return (
        <Dialog
            open
            onClose={loading ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
        >
            <Formik
                initialValues={{
                    price: rule?.price.toString() ?? '',
                    currency: rule?.currency ?? 'USD',
                }}
                validationSchema={validationSchema}
                onSubmit={({ price, currency }) => onSubmit({ price: Number(price), currency })}
            >
                <Box component={Form} noValidate>
                    <DialogTitle>
                        {translate(
                            rule
                                ? 'serviceProviderBilling.assessment.edit'
                                : 'serviceProviderBilling.assessment.set'
                        )}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2.5}>
                            <Typography variant="body2" color="text.secondary">
                                {translate('serviceProviderBilling.assessment.dialogDescription')}
                            </Typography>
                            <FormGridLayout>
                                <FormField
                                    type="number"
                                    name="price"
                                    label="price"
                                    required
                                    disabled={loading}
                                />
                                <FormField
                                    type="select"
                                    name="currency"
                                    label="currency"
                                    options={currencyOptions}
                                    required
                                    disabled={loading}
                                />
                            </FormGridLayout>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('cancel')}
                        </Button>
                        <SubmitButton loading={loading}>save</SubmitButton>
                    </DialogActions>
                </Box>
            </Formik>
        </Dialog>
    );
};

export default AssessmentBillingDialog;
